import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router';
import dropRight from 'lodash/dropRight';
import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy';
import startCase from 'lodash/startCase';
import find from 'lodash/find';
import moment from 'moment-timezone';
import IconCircle from 'shared/lib/components/IconCircle';
import Spinner from 'shared/lib/components/Spinner';
import VERTICALS from 'shared/lib/constants/verticals';
import { AdvanceList, AdvanceListItem } from '../../components/AdvanceList';
import * as analytics from 'shared/lib/analytics';
import './ClaimsPolicies.css';

class ClaimsPolicies extends Component {
  componentDidMount() {
    document.title = 'Claims | Select a policy - Sure';
    this.props.getPoliciesForVertical(this.props.params.verticalCode);
    analytics.page('Portal - Claim Vertical Purchases', {
      category: 'claim',
      value: this.verticalOrders.length,
    });
  }

  get verticalTitle() {
    let { description: title } = VERTICALS[this.props.params.verticalCode];
    return dropRight(title.split(' ')).join(' ');
  }

  get verticalOrders() {
    return sortBy(
      filter(this.props.policiesById, {
        vertical_code: this.props.params.verticalCode,
      }),
      'created'
    ).reverse();
  }

  get loadingStateNode() {
    return (
      <div className="ClaimsPolicies ClaimsPolicies--loadingState">
        <div className="Container Container--small">
          <Spinner />
          <h1 className="ClaimsPolicies-subheading">
            Fetching {this.verticalTitle} policies...
          </h1>
        </div>
      </div>
    );
  }

  get zeroStateNode() {
    return (
      <div className="ClaimsPolicies ClaimsPolicies--zeroState">
        <div className="Container Container--small">
          <IconCircle
            className="ClaimsPolicies-icon"
            icon="receipt"
            color="medium"
            background="gray"
          />
          <h1 className="ClaimsPolicies-subheading">No Purchases</h1>
          <p className="ClaimsPolicies-info">
            We don’t see any previously purchased policies for{' '}
            {this.verticalTitle}.
          </p>
          <Link
            className="ClaimsPolicies-backLink"
            to="/account/claims/new"
            onClick={this.trackBackClick}
          >
            Back to claim types
          </Link>
        </div>
      </div>
    );
  }

  get policiesListNode() {
    return (
      <div className="ClaimsPolicies ClaimsPolicies--zeroState">
        <div className="Container Container--small">
          <h1 className="ClaimsPolicies-heading">
            Select a policy from your order history.
          </h1>
          <AdvanceList>
            {filter(this.verticalOrders, 'policy_number').map((order, i) => {
              return (
                <AdvanceListItem
                  key={order.id}
                  linkTo={`/account/claims/new/${
                    this.props.params.verticalCode
                  }/policies/${order.policy_number}`}
                  title={`${
                    find(VERTICALS, { code: order.vertical_code }).title
                  } Policy (#${order.policy_number})`}
                  details={this.getDetailsForOrder(order)}
                  onClick={this.trackOrderClick.bind(this, order, i)}
                />
              );
            })}
          </AdvanceList>
        </div>
      </div>
    );
  }

  get contentNode() {
    if (this.props.isFetchingOrders) {
      return this.loadingStateNode;
    } else if (this.verticalOrders.length) {
      return this.policiesListNode;
    } else {
      return this.zeroStateNode;
    }
  }

  trackBackClick() {
    analytics.track('Portal - Claim Back to Claim Types Clicked', {
      category: 'claim',
    });
  }

  trackOrderClick(order, i) {
    analytics.track('Portal - Claim Policy Clicked', {
      category: 'claim',
      label: order.vertical_code,
      value: i,
    });
  }

  getDetailsForOrder(order) {
    const details = [
      `Purchased on ${moment(order.created).format('MMM D, YYYY')}`,
    ];
    switch (order.vertical_code) {
      case 'devices':
        details.push(order.details.modal_name);
        break;
      case 'pet':
        details.push(order.details.pets.map(pet => pet.name).join(', '));
        break;
      case 'collectibles':
        details.push(order.details.categories.join(', '));
        break;
      case 'commercial':
        details.push(order.details.business_name);
        break;
      case 'baggage':
      case 'flight':
        if (order.details.airline_code && order.details.flight_number) {
          details.push(
            `${order.details.airline_code}${order.details.flight_number}`
          );
        } else if (order.details.airline_name) {
          details.push(order.details.airline_name);
        }
        break;
      case 'landlord':
      case 'renters':
        details.push(
          `${startCase(order.details.address.line1)}${order.details.address
            .line2 && ', '}${startCase(order.details.address.line2)}, ${
            order.details.address.city
          } ${order.details.address.region}, ${order.details.address.postal}`
        );
        break;
      case 'rental_car':
        const pickUpDate = moment(order.details.rental_started).format(
          'MMM D, YYYY'
        );
        const returnDate = moment(order.details.rental_ended).format(
          'MMM D, YYYY'
        );
        details.push(`Rental period ${pickUpDate} to ${returnDate}`);
        break;
      case 'rideshare':
        const period = moment(order.details.coverage_start_date).format(
          'M/D/YYYY h:mm a'
        );
        const rideNoun = order.details.ride_count > 1 ? 'rides' : 'ride';
        details.push(`${order.details.ride_count} ${rideNoun} on ${period}`);
        break;
      default:
        break;
    }
    return details;
  }

  render() {
    return this.contentNode;
  }
}

const mapStateToProps = state => {
  return {
    isFetchingOrders: state.isFetchingOrders,
  };
};

export default connect(mapStateToProps)(withRouter(ClaimsPolicies));
